import React from 'react';
import styled from 'styled-components';
import { FolderOpen, Folder, Calendar, CheckCircle, Wallet, Briefcase, Award, Medal, Trophy, XCircle } from 'lucide-react';

interface CasesMetrics {
  activeCasesCount: number;
  committeesThisMonth: number;
  committeesThisWeek: number;
  approvedCasesThisMonth: number;
  rejectedCasesThisMonth: number;
  totalMonthlyBenefitsAchieved: number;
  totalRetroactiveBenefitsAchieved: number;
}

interface TopCaseManager {
  userId: number;
  name: string;
  casesManaged: number;
  approvedCases: number;
  totalMonthlyBenefits: number;
  expectedIncome: number;
}

interface Props {
  metrics: CasesMetrics;
  topCaseManagers: TopCaseManager[];
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color: string; $noBackground?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$noBackground ? 'transparent' : `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`;

const StatusBadge = styled.div<{ $type: 'approved' | 'rejected' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 10px;
  background: ${props => {
    if (props.$type === 'approved') return '#16a34a15';
    if (props.$type === 'rejected') return '#dc262615';
    return '#008EA215';
  }};
  color: ${props => {
    if (props.$type === 'approved') return '#16a34a';
    if (props.$type === 'rejected') return '#dc2626';
    return '#008EA2';
  }};
  filter: saturate(0.7) brightness(0.85);
`;

const HighlightValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #16a34a;
  margin-top: 10px;
  direction: ltr;
  text-align: right;
  filter: saturate(0.7) brightness(0.85);
`;

const TableContainer = styled.div`
  background: var(--background-color);
  border-radius: 10px;
  padding: 20px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const TableIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  filter: saturate(0.7) brightness(0.9);
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: white;
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  background: white;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover td {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const RankingBadge = styled.div<{ $rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-weight: 700;
  font-size: 14px;
  color: ${props => {
    if (props.$rank === 1) return '#d97706';
    if (props.$rank === 2) return '#6b7280';
    if (props.$rank === 3) return '#92400e';
    return 'var(--text-secondary)';
  }};
  filter: saturate(0.7) brightness(0.9);
`;

const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const AdminCasesMetrics: React.FC<Props> = ({ metrics, topCaseManagers }) => {
  const approvalRate =
    metrics.approvedCasesThisMonth + metrics.rejectedCasesThisMonth > 0
      ? (
          (metrics.approvedCasesThisMonth /
            (metrics.approvedCasesThisMonth + metrics.rejectedCasesThisMonth)) *
          100
        ).toFixed(1)
      : '0.0';

  const getRankingIcon = (rank: number) => {
    if (rank === 1) return <Trophy size={14} />;
    if (rank === 2) return <Medal size={14} />;
    if (rank === 3) return <Award size={14} />;
    return rank;
  };

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <FolderOpen size={20} />
        </SectionIcon>
        <SectionTitle>ניהול תיקים וזכאות</SectionTitle>
      </SectionHeader>

      <CardsGrid>
        <Card $accentColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <Folder size={20} />
            </CardIcon>
            <CardTitle>תיקים פעילים</CardTitle>
          </CardHeader>
          <CardValue>{metrics.activeCasesCount}</CardValue>
          <CardSubValue>תיקים בטיפול</CardSubValue>
        </Card>

        <Card $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1" $noBackground>
              <Calendar size={20} />
            </CardIcon>
            <CardTitle>ועדות</CardTitle>
          </CardHeader>
          <CardValue>{metrics.committeesThisMonth}</CardValue>
          <CardSubValue>החודש</CardSubValue>
          <StatusBadge $type="info">
            {metrics.committeesThisWeek} השבוע הקרוב
          </StatusBadge>
        </Card>

        <Card $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <CheckCircle size={20} />
            </CardIcon>
            <CardTitle>תיקים מאושרים</CardTitle>
          </CardHeader>
          <CardValue>{metrics.approvedCasesThisMonth}</CardValue>
          <CardSubValue>החודש ({approvalRate}% מהוועדות)</CardSubValue>
          {metrics.rejectedCasesThisMonth > 0 && (
            <StatusBadge $type="rejected">
              <XCircle size={12} />
              {metrics.rejectedCasesThisMonth} נדחו
            </StatusBadge>
          )}
        </Card>

        <Card $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Wallet size={20} />
            </CardIcon>
            <CardTitle>גמלאות שהושגו</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(metrics.totalMonthlyBenefitsAchieved)}</CardValue>
          <CardSubValue>לחודש</CardSubValue>
          {metrics.totalRetroactiveBenefitsAchieved > 0 && (
            <HighlightValue>
              + {formatCurrency(metrics.totalRetroactiveBenefitsAchieved)} רטרואקטיבי
            </HighlightValue>
          )}
        </Card>
      </CardsGrid>

      {topCaseManagers && topCaseManagers.length > 0 && (
        <TableContainer>
          <TableHeader>
            <TableIcon>
              <Briefcase size={18} />
            </TableIcon>
            <TableTitle>מנהלי תיקים מצטיינים - TOP 5</TableTitle>
          </TableHeader>
          <Table>
            <thead>
              <tr>
                <Th>דירוג</Th>
                <Th>שם</Th>
                <Th>תיקים מנוהלים</Th>
                <Th>אושרו החודש</Th>
                <Th>גמלאות שהושגו</Th>
                <Th>הכנסה צפויה</Th>
              </tr>
            </thead>
            <tbody>
              {topCaseManagers.map((manager, index) => (
                <Tr key={manager.userId}>
                  <Td>
                    <RankingBadge $rank={index + 1}>
                      {getRankingIcon(index + 1)}
                    </RankingBadge>
                  </Td>
                  <Td style={{ fontWeight: 600 }}>
                    {manager.name}
                  </Td>
                  <Td>{manager.casesManaged}</Td>
                  <Td style={{ fontWeight: 600, color: '#15803d' }}>
                    {manager.approvedCases}
                  </Td>
                  <Td style={{ direction: 'ltr', textAlign: 'center' }}>
                    {formatCurrency(manager.totalMonthlyBenefits)}
                  </Td>
                  <Td
                    style={{
                      fontWeight: 600,
                      color: '#1d4ed8',
                      direction: 'ltr',
                      textAlign: 'center',
                    }}
                  >
                    {formatCurrency(manager.expectedIncome)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminCasesMetrics;
